import {
  ColDef,
  ColGroupDef,
  ISetFilterParams,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { IAggFuncParams } from '@ag-grid-community/core/dist/esm/es6/entities/colDef';

import { rawAttributeColumnRenderer } from '@shared/components/ag-raw-attribute-renderer/ag-raw-attribute-renderer';
import { AgRawTooltip } from '@shared/components/ag-raw-tooltip/ag-raw-tooltip';
import { TableConstants } from '@shared/constants/table.constants';
import { BudgetActivityAttributes, TrialAttributes } from '@shared/services/gql.service';
import { AuxExcelStyleKeys } from '@shared/utils';
import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';
import { Utils } from '@shared/utils/utils';

const MAX_COLUMNS_LENGTH = 13; // Custom Attributes + PO #

export const getAttributeColumns = ({
  attributes: allAttributes,
}: {
  attributes: BudgetActivityAttributes[][];
}) => {
  const usedColumns: BudgetActivityAttributes[] = [
    {
      attribute_name: 'PO #',
    } as BudgetActivityAttributes,
  ];

  for (const attributes of allAttributes) {
    const usedNames = usedColumns.map((usedAttr) => usedAttr.attribute_name);
    const remainingAttributes = attributes
      .filter((attr) => !usedNames.includes(attr.attribute_name))
      .slice()
      .sort((a, b) => Utils.localeAlphaNumSort(a.attribute_name || '', b.attribute_name || ''));

    if (remainingAttributes.length) {
      usedColumns.push(...remainingAttributes.slice(0, MAX_COLUMNS_LENGTH - usedColumns.length));
    }

    if (usedColumns.length === MAX_COLUMNS_LENGTH) {
      break;
    }
  }

  return usedColumns;
};

// todo if you are deleting this function, you should also delete the old-attribute-cell class.
export const attributeColumnDef = <T>(
  attributes: BudgetActivityAttributes[],
  localStorageKey: string,
  gridData: T[],
  applyExelStyles?: boolean
): ColGroupDef => {
  const hide = localStorage.getItem(localStorageKey) === 'false';

  const baseChildColumnDef = {
    // sorting used in the compare component
    comparator: (a, b) => {
      return Utils.localeAlphaNumSort(a || '', b || '');
    },
    filter: true,
    filterParams: {
      showTooltips: true,
    } as ISetFilterParams,
    headerClass: 'ag-header-align-center',
    hide,
    menuTabs: ['filterMenuTab'],
    minWidth: 150,
    resizable: true,
    suppressMenu: false,
    width: 150,
  } as ColDef;

  const children: ColDef[] = attributes
    .map((attr, index, array) => {
      const name = attr.attribute_name || '';

      return {
        ...baseChildColumnDef,
        field: getEncodedAttributeName(name),
        headerClass: getAttributeHeaderClasses(name, !!applyExelStyles, index, array),
        headerName: name,
        headerTooltip: name,
        tooltipComponent: AgRawTooltip,
        tooltipField: getEncodedAttributeName(name),
        ...rawAttributeColumnRenderer({ gridData }, name, index),
        aggFunc: (params: IAggFuncParams) => {
          // if all the value are the same return that value:
          const values = params.values as string[];

          if (values.every((x) => x === values[0])) {
            return values[0];
          }

          return '';
        },
        cellClass: [
          'old-attribute-cell',
          'grid-cell',
          applyExelStyles && index === 0 ? AuxExcelStyleKeys.BORDER_LEFT : '',
          applyExelStyles && index === array.length - 1 ? AuxExcelStyleKeys.BORDER_RIGHT : '',
        ],
        tooltipValueGetter: (params: ValueFormatterParams) => {
          return params.value || params.node?.aggData?.[params.colDef.field || ''] || '';
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return params.value || params.node?.aggData?.[params.colDef.field || ''] || '';
        },
      };
    })
    .filter((z) => z) as ColDef[];

  return {
    children,
    groupId: 'attributes',
    headerClass: [TableConstants.STYLE_CLASSES.HEADER_ALIGN_CENTER, 'attribute-header'],
    headerName: 'Attributes',
    hide: false,
    tooltipComponent: AgRawTooltip,
  } as ColGroupDef;
};

export const getTrialAttributes = (
  attributes: TrialAttributes[],
  primaryAttribute = '',
  secondaryAttribute = ''
) =>
  attributes
    .filter(
      (attr) =>
        attr.attribute_name !== primaryAttribute && attr.attribute_name !== secondaryAttribute
    )
    .sort((a, b) => {
      if (a.attribute_name === 'PO #') return -1;

      if (b.attribute_name === 'PO #') return 1;

      return Utils.localeAlphaNumSort(a.attribute_name, b.attribute_name);
    })
    .slice(0, MAX_COLUMNS_LENGTH);

export const getAttributeHeaderClasses = (
  name: string,
  applyExelStyles: boolean,
  index: number,
  array: BudgetActivityAttributes[]
) => {
  return [
    'ag-header-align-center',
    name.includes(' ') ? 'ag-header-clamp' : 'ag-header-truncate',
    applyExelStyles && index === 0 ? AuxExcelStyleKeys.BORDER_LEFT : '',
    applyExelStyles && index === array.length - 1 ? AuxExcelStyleKeys.BORDER_RIGHT : '',
  ];
};
